// ─── BadgeEmbedCard.tsx — Badge de score embarquable (backlink/SEO) ──────────
//
// Affiché après un scan réussi, pour TOUT LE MONDE (anonymes compris), sur le
// domaine scanné. Le badge SVG public (/public/badge/{domain}) reflète le
// dernier score connu ; son snippet d'intégration est enveloppé dans un lien
// vers wezea.net (UTM) → chaque badge posé ailleurs devient un backlink.
//
import { useState } from 'react';
import { ShieldCheck, Copy, Check } from 'lucide-react';
import SkuIcon from './SkuIcon';
import { useLanguage } from '../i18n/LanguageContext';
import { captureBadgeEmbedCopied } from '../lib/analytics';

interface BadgeEmbedCardProps {
  domain: string;
}

// Origine publique du badge SVG (servie par le backend sur scan.wezea.net/public/badge).
const BADGE_ORIGIN = 'https://scan.wezea.net/public/badge';

export default function BadgeEmbedCard({ domain }: BadgeEmbedCardProps) {
  const { lang } = useLanguage();
  const [copied, setCopied] = useState<'markdown' | 'html' | null>(null);

  const siteUrl   = `https://wezea.net/?utm_source=badge&utm_medium=embed&utm_campaign=${encodeURIComponent(domain)}`;
  const badgeSrc  = `${BADGE_ORIGIN}/${domain}`;
  const embedMd   = `[![Security Score](${badgeSrc})](${siteUrl})`;
  const embedHtml = `<a href="${siteUrl}"><img src="${badgeSrc}" alt="Security Score — Wezea" /></a>`;

  function copy(format: 'markdown' | 'html', value: string) {
    try { navigator.clipboard.writeText(value); } catch { /* clipboard indispo */ }
    captureBadgeEmbedCopied(format);
    setCopied(format);
    setTimeout(() => setCopied(null), 2000);
  }

  return (
    <div className="sku-card rounded-xl p-5 mb-2">
      <div className="flex items-center gap-3 mb-1">
        <SkuIcon color="#0d9488" size={32}>
          <ShieldCheck size={14} className="text-[var(--color-accent-strong)]" />
        </SkuIcon>
        <h3 className="text-[var(--color-ink)] font-semibold text-sm">
          {lang === 'fr' ? 'Badge de score' : 'Score badge'}
        </h3>
      </div>
      <p className="text-[var(--color-muted)] text-xs mb-4">
        {lang === 'fr'
          ? 'Affichez votre score de sécurité en temps réel sur votre site ou votre README — gratuit.'
          : 'Display your real-time security score on your website or README — free.'}
      </p>

      <div className="flex items-center gap-3 mb-4">
        {/* Aperçu live : même URL absolue que le snippet → WYSIWYG. */}
        <img
          src={badgeSrc}
          alt={`Security score for ${domain}`}
          className="h-5"
          loading="lazy"
        />
        <span className="text-[var(--color-faint)] text-[11px] font-mono truncate">{domain}</span>
      </div>

      <div className="flex flex-col gap-1.5">
        <div>
          <p className="text-[var(--color-faint)] text-[10px] mb-0.5 uppercase font-mono tracking-wider">Markdown</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 min-w-0 text-[10px] text-[var(--color-muted)] font-mono bg-[var(--color-surface)] rounded px-2 py-1 overflow-hidden text-ellipsis whitespace-nowrap">{embedMd}</code>
            <button
              onClick={() => copy('markdown', embedMd)}
              className="p-1.5 rounded border border-[var(--color-border)] hover:border-[var(--color-border-strong)] text-[var(--color-muted)] hover:text-[var(--color-accent-strong)] transition shrink-0"
            >
              {copied === 'markdown' ? <Check size={11} className="text-emerald-500" /> : <Copy size={11} />}
            </button>
          </div>
        </div>
        <div>
          <p className="text-[var(--color-faint)] text-[10px] mb-0.5 uppercase font-mono tracking-wider">HTML</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 min-w-0 text-[10px] text-[var(--color-muted)] font-mono bg-[var(--color-surface)] rounded px-2 py-1 overflow-hidden text-ellipsis whitespace-nowrap">{embedHtml}</code>
            <button
              onClick={() => copy('html', embedHtml)}
              className="p-1.5 rounded border border-[var(--color-border)] hover:border-[var(--color-border-strong)] text-[var(--color-muted)] hover:text-[var(--color-accent-strong)] transition shrink-0"
            >
              {copied === 'html' ? <Check size={11} className="text-emerald-500" /> : <Copy size={11} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
